import { ColDef, ICellRendererParams, RowClickedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import React, { useCallback, useMemo } from 'react';
import { commonBorderString, SmallTitleWithSubSpan } from 'src/components/common/common';
import Grid_Excel from 'src/components/common/gridWrapper/Grid_Excel';
import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import { useDetailTableEvents } from 'src/hooks/detail-table/useDetailTableEvents';
import { CommonCodeColumn } from 'src/utils/grid/GridColumnContent';
import { useGridSettingContent } from 'src/utils/grid/GridSettingContent';
import { GridCustomColumnDefs } from 'src/utils/grid/GridCustomColumnDefs';
import {
    CreateCellClickHandler,
    CustomCellClickAction,
    createCellCssClassRules,
    createGridDropDownCommitAfter,
    GridCellConfig,
} from 'src/utils/grid/GridHandlerContent';
import { buildDefaultColumnStateFromDefs } from 'src/utils/grid/columnPinUtils';
import { defaultColDef, getDefaultRowIndexColumnConfig, getRowStyleFlags } from 'src/utils/grid/gridStyleUtils';
import { defaultHRM2010_R01DetailDto, HRM2010_R01DetailDto } from './Dto_HRM2010_R01';
import { useHRM2010_R01CellChange } from './hooks/HRM2010_R01CellChange';
import { useHRM2010_R01Store } from './Store_HRM2010_R01';
import { buildHRM2010_R01OutPairList, formatHRM2010_R01OutSummary, formatHRM2010_R01Time } from './Util_HRM2010_R01';

/** 외출 기록 그리드의 참조와 버튼 함수이다. */
interface Props {
    gridRef: React.RefObject<AgGridReact<HRM2010_R01DetailDto> | null>;
    handleInsertClick: (event: React.MouseEvent, rowIndex?: number) => void;
    handleDeleteClick: () => void;
    handleEventAddClick: (attendCd: string) => void;
    setActiveGridType: (value: 'header' | 'detail') => void;
}

/** 외출 그리드의 편집 대상과 열 배치 보관 키이다. */
const EDITABLE_COLUMNS = ['attendCd', 'eventTime', 'remark'];
const COLUMN_STATE_KEY = 'HRM2010_R01-detail-grid-column-state';
const HEADER_NAMES = {
    rowIndex: '',
    eventSeq: '순번',
    attendCd: '구분',
    eventTime: '시각',
    workYn: '근무 처리',
    remark: '사유',
};

/** 외출·복귀 짝 요약과 편집 가능한 기록을 표시한다. */
export const DetailTable_HRM2010_R01 = ({
    gridRef,
    handleInsertClick,
    handleDeleteClick,
    handleEventAddClick,
    setActiveGridType,
}: Props) => {
    const state = useHRM2010_R01Store();
    /** 직접 입력 셀 변경, 구분 드롭다운 선택, 위 행 재계산 함수를 가져온다. */
    const { handleDetailCellChange, handleDetailDropDownChange, calculateHRM2010_R01Row } = useHRM2010_R01CellChange();
    /** 구분 드롭다운에서 Enter·Tab으로 선택하면 다음 편집 칸을 연다. 편집 컬럼이 상수라 처음 한 번만 만든다. */
    const handleGridDropDownCommitAfter = useMemo(
        () => createGridDropDownCommitAfter<HRM2010_R01DetailDto>(EDITABLE_COLUMNS),
        [],
    );
    const {
        getRowId,
        renderIndexCell,
        createRowClassRules,
        createCellClassRules,
        paddedGridData,
    } = useGridSettingContent<HRM2010_R01DetailDto>({
        gridRef,
        key: 'valueIndex',
        gridData: state.HRM2010_R01DetailList,
        defaultGridData: defaultHRM2010_R01DetailDto,
        selectedRow: state.selectedHRM2010_R01DetailList,
        originalGridData: state.originalHRM2010_R01DetailList,
        updateRow: state.updateHRM2010_R01DetailList,
    });
    const editable = useCallback(
        (row?: HRM2010_R01DetailDto) => !!row && !!state.selectedHRM2010_R01
            && !state.deletedHRM2010_R01DetailList.includes(String(row.valueIndex)),
        [state.selectedHRM2010_R01, state.deletedHRM2010_R01DetailList],
    );
    const handleRemoveRow = useCallback((row: HRM2010_R01DetailDto) => {
        if (!row.inserted) return;
        state.removeHRM2010_R01DetailList(row.valueIndex);
        state.setSelectedHRM2010_R01DetailList(
            state.selectedHRM2010_R01DetailList.filter(id => id !== String(row.valueIndex)),
        );
        const current = useHRM2010_R01Store.getState();
        if (current.selectedHRM2010_R01) {
            const activeEvents = current.HRM2010_R01DetailList.filter(value =>
                !current.deletedHRM2010_R01DetailList.includes(String(value.valueIndex)));
            void calculateHRM2010_R01Row(current.selectedHRM2010_R01, activeEvents);
        }
    }, [state, calculateHRM2010_R01Row]);
    /** 공통 index 셀에 삽입·신규 행 제거 버튼을 연결한다. */
    const renderDetailIndexCell = useCallback(
        (params: ICellRendererParams<HRM2010_R01DetailDto>) => renderIndexCell(
            params,
            innerParams => ({
                ...getRowStyleFlags(innerParams, {
                    deletedList: state.deletedHRM2010_R01DetailList,
                    selectedList: state.selectedHRM2010_R01DetailList,
                    dataList: state.HRM2010_R01DetailList,
                }),
                isDeleted: state.deletedHRM2010_R01DetailList.includes(String(innerParams.data?.valueIndex)),
                isSelected: state.selectedHRM2010_R01DetailList.includes(String(innerParams.data?.valueIndex)),
            }),
            (rowIndex: number, event: React.MouseEvent) => handleInsertClick(event, rowIndex),
            handleRemoveRow,
        ),
        [renderIndexCell, state, handleInsertClick, handleRemoveRow],
    );
    /** 설계의 업무 순번·구분·시각·근무 처리·사유 열이다. */
    const baseColumnDefs = useMemo<ColDef<HRM2010_R01DetailDto>[]>(() => [
        ...getDefaultRowIndexColumnConfig(renderDetailIndexCell),
        {
            headerName: '순번', colId: 'eventSeq', field: 'eventSeq', width: 52,
            valueFormatter: params => String(params.value || ''),
        },
        {
            headerName: '구분', colId: 'attendCd', field: 'attendCd', width: 100,
            // 공통 드롭다운 옵션을 펼친다. 외출·복귀 계열(G5·G6) 근태코드만 공통코드 모양(minorCd·codeNm)으로 바꿔 넘긴다.
            // 선택 시 근태 표시값 갱신과 위 행 재계산이 필요하므로 change 대신 화면 Hook의 함수를 연결한다.
            ...CommonCodeColumn.options<HRM2010_R01DetailDto>(
                state.attendCodeList
                    .filter(row => ['G5', 'G6'].includes(row.attendGroup))
                    .map(row => ({ minorCd: row.attendCd, codeNm: row.attendNm })),
                params => editable(params.data),
                handleDetailDropDownChange,
                handleGridDropDownCommitAfter,
            ),
            cellRenderer: (params: ICellRendererParams<HRM2010_R01DetailDto>) => {
                if (!params.value) return null;
                const label = state.attendCodeList.find(row => row.attendCd === params.value)?.attendNm
                    || String(params.value);
                const tone = params.value === 'O01' ? 'border-red-200 bg-red-50 text-red-700'
                    : params.value === 'O02' ? 'border-blue-200 bg-blue-50 text-blue-700'
                    : 'border-green-200 bg-green-50 text-green-700';
                return <span className={`rounded border px-1.5 py-0.5 text-xs ${tone}`}>{label}</span>;
            },
        },
        {
            headerName: '시각', colId: 'eventTime', field: 'eventTime', width: 100,
            editable: params => editable(params.data),
            cellEditor: 'agTextCellEditor',
            cellEditorParams: { maxLength: 5 },
            // 0930처럼 숫자 4자리로 입력해도 저장 전에 09:30으로 바꾼다.
            valueParser: params => formatHRM2010_R01Time(params.newValue),
            valueFormatter: params => params.node?.rowPinned ? '' : params.value || 'HH:MM',
            cellStyle: params => ({
                textAlign: 'center',
                color: params.value ? 'inherit' : '#9ca3af',
            }),
        },
        {
            headerName: '근무 처리', colId: 'workYn', field: 'workYn', width: 120,
            valueFormatter: params => params.node?.rowPinned
                ? '' : params.data?.workYn === 'Y' ? '근무시간 인정' : '근무시간 제외',
            cellStyle: params => ({ color: params.data?.workYn === 'Y' ? 'inherit' : '#dc2626' }),
        },
        {
            headerName: '사유', colId: 'remark', field: 'remark', width: 180,
            editable: params => editable(params.data),
        },
    ], [renderDetailIndexCell, editable, state.attendCodeList,
        handleDetailDropDownChange, handleGridDropDownCommitAfter]);
    const defaultColumnState = useMemo(
        () => buildDefaultColumnStateFromDefs(baseColumnDefs, []),
        [baseColumnDefs],
    );
    const columnDefs = useMemo(() => {
        if (state.detailColumnState === null) return baseColumnDefs;

        const restored = GridCustomColumnDefs<HRM2010_R01DetailDto>({
            baseColumnDefs,
            columnState: state.detailColumnState,
            headerNames: HEADER_NAMES,
        });
        return restored.some(column => 'colId' in column && column.colId === 'rowIndex')
            ? restored : [baseColumnDefs[0], ...restored];
    }, [baseColumnDefs, state.detailColumnState]);
    const cellCssConfig = useMemo<GridCellConfig>(() => ({
        disabledCells: [],
        insertedDisabledCells: [],
        commonDisabledCells: ['eventSeq', 'workYn'],
        pointerCells: ['attendCd', 'eventTime', 'remark'],
        insertedPointerCells: [],
        commonPointerCells: [],
        alignLeftCells: ['remark'],
    }), []);
    const cellCssColumns = useMemo(() => createCellCssClassRules(cellCssConfig), [cellCssConfig]);
    const selectedIds = useMemo(
        () => state.selectedHRM2010_R01DetailList.map(Number),
        [state.selectedHRM2010_R01DetailList],
    );
    const cellCssDefaultColDef = useMemo(() => ({
        ...defaultColDef,
        cellClassRules: createCellClassRules<HRM2010_R01DetailDto>({
            idKey: 'valueIndex',
            selectedList: selectedIds,
            deletedList: state.deletedHRM2010_R01DetailList.map(Number),
            ...cellCssColumns,
        }),
    }), [createCellClassRules, selectedIds, state.deletedHRM2010_R01DetailList, cellCssColumns]);
    const cellEventHandlers = useDetailTableEvents({
        detailList: state.HRM2010_R01DetailList,
        editableColumns: EDITABLE_COLUMNS,
        keyDownConfig: { enableArrowNavigation: true },
    });
    const cellClickActions = useMemo<CustomCellClickAction<HRM2010_R01DetailDto>[]>(() => [{
        validate: (field, row) => EDITABLE_COLUMNS.includes(field) && editable(row),
        action: event => event.api.startEditingCell({ rowIndex: event.rowIndex!, colKey: event.colDef.field! }),
    }], [editable]);
    const handleCellClick = CreateCellClickHandler(state.HRM2010_R01DetailList, 'valueIndex', cellClickActions);
    const handleRowClick = useCallback((event: RowClickedEvent<HRM2010_R01DetailDto>) => {
        if (event.node.rowPinned || !event.data) return;
        setActiveGridType('detail');
        const rowId = String(event.data.valueIndex);
        const mouse = event.event as MouseEvent | undefined;
        if (!mouse?.ctrlKey && !mouse?.metaKey) {
            state.setSelectedHRM2010_R01DetailList([rowId]);
            return;
        }
        state.setSelectedHRM2010_R01DetailList(state.selectedHRM2010_R01DetailList.includes(rowId)
            ? state.selectedHRM2010_R01DetailList.filter(id => id !== rowId)
            : [...state.selectedHRM2010_R01DetailList, rowId]);
    }, [state, setActiveGridType]);
    const activeEvents = useMemo(() => state.HRM2010_R01DetailList.filter(row =>
        !state.deletedHRM2010_R01DetailList.includes(String(row.valueIndex))),
    [state.HRM2010_R01DetailList, state.deletedHRM2010_R01DetailList]);
    const pinnedBottomRow = useMemo(
        () => [{ eventSeq: `${activeEvents.length}건` }],
        [activeEvents.length],
    );
    const pairs = useMemo(() => {
        const selected = state.selectedHRM2010_R01;
        return selected ? buildHRM2010_R01OutPairList(
            activeEvents, selected.startTime, selected.endTime, state.attendCodeList,
        ) : [];
    }, [activeEvents, state.selectedHRM2010_R01, state.attendCodeList]);
    return (
        <section className="flex flex-col h-full min-h-0">
            {/* 공통 제목 오른쪽에 기존 선택 사원 정보와 외출·공용외출·복귀 버튼을 유지한다. */}
            <SmallTitleWithSubSpan title="외출 · 복귀 기록" subTitle={
                    <span className="flex flex-wrap items-center justify-end gap-1 text-xs font-normal text-gray-600">
                        <span>
                            {state.selectedHRM2010_R01
                                ? `${state.selectedHRM2010_R01.empNo} ${state.selectedHRM2010_R01.empNm} · ${state.selectedHRM2010_R01.workDt}`
                                : ''}
                        </span>
                        {(['O01', 'O02', 'O03'] as const).map(code => (
                            <button
                                key={code}
                                type="button"
                                className="rounded border px-2 py-1"
                                onClick={() => handleEventAddClick(code)}
                            >
                                {code === 'O01' ? '외출' : code === 'O02' ? '공용외출' : '복귀'}
                            </button>
                        ))}
                    </span>
            } />
            {/* 외출·복귀 짝과 서버 시간 값으로 만든 표시용 요약이다. 제목 박스와 그리드 사이 좌우 테두리가 끊기지 않도록 공통 테두리를 이어 그린다. */}
            <div className="flex flex-wrap gap-1 px-2 py-1 text-xs text-gray-600"
                style={{ borderLeft: commonBorderString, borderRight: commonBorderString }}>
                {!state.selectedHRM2010_R01 ? '위에서 일일근태 행을 선택하세요.' : <>
                    {activeEvents.length ? pairs.map((pair, index) => (
                        <span key={index} className="rounded bg-gray-100 px-1.5 py-0.5">
                            {pair.fromTime || '?'} → {pair.toTime || '?'} {pair.errorMessage || ''}
                        </span>
                    )) : '외출 기록이 없습니다.'}
                    <span>{formatHRM2010_R01OutSummary(state.selectedHRM2010_R01)}</span>
                </>}
            </div>
            {/* 공통 편집 그리드와 하단 건수 행이다. */}
            <GridContextWrapper<HRM2010_R01DetailDto>
                gridRef={gridRef}
                paddedGridData={paddedGridData}
                initSelected={() => state.setSelectedHRM2010_R01DetailList([])}
                allSelected={() => state.setSelectedHRM2010_R01DetailList(
                    activeEvents.map(row => String(row.valueIndex)))}
                defaultRowData={defaultHRM2010_R01DetailDto}
                baseColumnDefs={columnDefs}
                defaultColumnState={defaultColumnState}
                setColumnState={state.setDetailColumnState}
                localStorageKey={COLUMN_STATE_KEY}
            >
                <Grid_Excel<HRM2010_R01DetailDto>
                    ref={gridRef}
                    rowData={paddedGridData}
                    getRowId={getRowId}
                    columnDefs={columnDefs}
                    defaultColDef={cellCssDefaultColDef}
                    rowClassRules={createRowClassRules<HRM2010_R01DetailDto>({
                        idKey: 'valueIndex',
                        selectedList: selectedIds,
                        deletedList: state.deletedHRM2010_R01DetailList.map(Number),
                    })}
                    onRowClicked={handleRowClick}
                    onCellClicked={handleCellClick}
                    onCellKeyDown={cellEventHandlers.handleCellKeyDown}
                    onCellValueChanged={handleDetailCellChange}
                    pinnedBottomRowData={pinnedBottomRow}
                    columnStateKey={COLUMN_STATE_KEY}
                    setColumnState={state.setDetailColumnState}
                    getDefaultColumnState={() => defaultColumnState}
                    scrollTop={state.detailScrollTop}
                    setScrollTop={state.setDetailScrollTop}
                />
            </GridContextWrapper>
        </section>
    );
};
